import type { TaskKey } from '../hooks/useOnboarding';
import type { DriveStep } from 'driver.js';

/**
 * Definizioni dei tour driver.js per ogni task della SetupChecklist.
 *
 * Quando l'utente clicca un task nella checklist, viene navigato alla
 * pagina target con query param `?tour=<taskId>`. L'hook globale
 * `useDriverTour` lo intercetta e lancia il tour corrispondente.
 *
 * Convenzione selettori: prediligi `[data-tour="..."]` attributi su elementi
 * della UI per stabilita. Fallback a selettori CSS classici quando l'elemento
 * non puo essere taggato (es. componente library third-party).
 *
 * Quando aggiungi uno step, ricordati di marcare il target con `data-tour=...`
 * nel componente UI corrispondente (search global con grep prima di assumere
 * un selettore esistente).
 */

export interface TourDefinition {
  /**
   * Per i tour della checklist di onboarding e' un `TaskKey` reale (vedi
   * PATH_RULES in useContextualTourTrigger e la SetupChecklist). Per i tour
   * "inline" lanciati direttamente da un componente via `launchInlineTour`
   * (es. `drawer-redesign`) e' un id libero, mai un `TaskKey`: questi tour non
   * fanno parte della checklist e non passano mai dal query param `?tour=`.
   */
  taskId: TaskKey | string;
  steps: DriveStep[];
  /** Quando true, alla chiusura del tour marca il task come completato. Mai true per i tour inline. */
  completeOnFinish?: boolean;
}

/**
 * Copy guideline: ogni step deve rispondere a "perche farlo?" velocemente.
 * Pattern: title diretto (max ~30 chars) + description action+why (max ~220 chars).
 * Evitiamo "clicca qui" — preferiamo il valore dietro l'azione.
 * Driver.js renderizza HTML nella description (uso <strong>...</strong>).
 */
// Mappa chiave-libera: contiene sia i tour della checklist (chiave = TaskKey,
// consumati da useContextualTourTrigger/useDriverTour via ?tour=) sia i tour
// "inline" (chiave = id libero, es. 'drawer-redesign', consumati SOLO da
// launchInlineTour). getTourDefinition() legge indistintamente da entrambe.
export const TOUR_DEFINITIONS: Record<string, TourDefinition> = {
  profile: {
    taskId: 'profile',
    steps: [
      {
        element: '[data-tour="company-edit-btn"]',
        popover: {
          title: "Configura l'identita della tua azienda",
          description:
            'Inserisci ragione sociale, partita IVA, sede e contatti. <strong>Perche conta:</strong> questi dati compaiono automaticamente nelle fatture, nei preventivi e nelle email che mandi ai clienti. Riempirli ora ti evita di rimetterli ogni volta o di inviare documenti incompleti.',
          side: 'left',
          align: 'start',
        },
      },
    ],
  },
  pipeline: {
    taskId: 'pipeline',
    steps: [
      {
        element: '[data-tour="pipeline-list"]',
        popover: {
          title: 'Cosa sono le pipeline',
          description:
            'Una pipeline e la <strong>lista dei passaggi</strong> che fa ogni trattativa, dal primo contatto fino alla vendita chiusa (o persa). Esempio: <em>Lead &rarr; Contattato &rarr; Preventivo &rarr; Vinta</em>. <strong>Perche conta:</strong> senza un percorso chiaro rischi di dimenticare clienti a meta strada. Qui invece vedi sempre a che punto sei con ognuno.',
          side: 'top',
        },
      },
      {
        element: '[data-tour="pipeline-add-btn"]',
        popover: {
          title: 'Crea la tua prima pipeline',
          description:
            'Clicca per costruire il percorso che usi davvero in azienda. <strong>Perche conta:</strong> se vendi cose diverse (servizi e prodotti, piccoli e grandi), ognuna ha tempi e passaggi suoi. Una pipeline per ogni tipo di vendita ti tiene tutto in ordine — niente trattative perse nel mucchio.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="pipeline-stages-area"]',
        popover: {
          title: 'Dai un nome ai passaggi',
          description:
            'Espandi una pipeline per vedere e modificare i suoi passaggi (chiamati <em>stati</em>). <strong>Perche conta:</strong> chiamali con le parole che usi in ufficio — "Aspetto risposta dal cliente" funziona meglio di "Negoziazione". Quando i nomi sono i tuoi, il team li capisce al volo e li usa davvero.',
          side: 'right',
        },
      },
    ],
  },
  goal: {
    taskId: 'goal',
    steps: [
      {
        element: '[data-tour="sales-target-add-btn"]',
        popover: {
          title: "Cos'e un obiettivo di vendita",
          description:
            'Un obiettivo (chiamato anche <em>target</em>) e un numero che dici di voler raggiungere — per esempio: <em>50.000 EUR di vendite entro fine mese</em>. Clicca qui per crearne uno. <strong>Perche conta:</strong> averlo scritto ti permette di vedere ogni giorno quanto ti manca, e da una direzione chiara al team: chiudere abbastanza trattative per arrivarci.',
          side: 'bottom',
          align: 'end',
        },
      },
    ],
  },
  integrations: {
    taskId: 'integrations',
    steps: [
      {
        element: '[data-tour="integrations-grid"]',
        popover: {
          title: 'Cosa sono le integrazioni',
          description:
            'Le integrazioni sono i <strong>collegamenti</strong> tra Flusia e altri programmi che gia usi: Gmail per le email, WhatsApp per le chat, Fatture in Cloud per le fatture, Meta Ads per i lead da Facebook e Instagram. <strong>Perche conta:</strong> senza collegamenti devi copiare e incollare a mano. Con le integrazioni i dati passano da soli — risparmi ore di lavoro ogni settimana ed eviti dimenticanze.',
          side: 'top',
        },
      },
    ],
  },
  lead: {
    taskId: 'lead',
    steps: [
      {
        element: '[data-tour="new-lead-btn"]',
        popover: {
          title: 'Crea il tuo primo lead',
          description:
            'Un <strong>lead</strong> e una persona o un\'azienda che ha mostrato interesse per quello che vendi, ma non ha ancora comprato. Esempi: chi ha lasciato l\'email sul tuo sito, chi ti ha scritto su Instagram, chi hai conosciuto a una fiera. Clicca qui per aggiungerne uno a mano. <strong>Perche conta:</strong> ogni cliente che paga e stato prima un lead. Salvarli in Flusia ti permette di ricontattarli al momento giusto e capire quali canali ti portano i clienti migliori.',
          side: 'left',
        },
      },
    ],
  },
  team: {
    taskId: 'team',
    steps: [
      {
        element: '[data-tour="invite-user-btn"]',
        popover: {
          title: 'Invita i tuoi colleghi',
          description:
            'Aggiungi le persone con cui lavori in azienda. Ognuna ha un proprio accesso (email + password) e un <em>ruolo</em>: agente vede e gestisce i propri clienti, manager segue il team, amministratore configura tutto. <strong>Perche conta:</strong> con piu mani sui lead chiudi piu vendite. I ruoli garantiscono che ognuno veda solo cio che gli serve — niente dati riservati in giro.',
          side: 'left',
        },
      },
    ],
  },
  referral: {
    taskId: 'referral',
    steps: [
      {
        element: '[data-tour="referral-code-card"]',
        popover: {
          title: 'Invita un\'altra azienda',
          description:
            'Qui c\'e il <strong>tuo codice personale</strong>. Condividilo con altre aziende che potrebbero trovarsi bene con Flusia: quando si iscrivono e pagano la prima volta, otterrete <strong>entrambi un mese gratis</strong>. <strong>Perche conta:</strong> il passaparola tra colleghi che si fidano e il modo piu naturale per crescere — tu vieni premiato per ogni azienda che porti, loro partono con uno sconto. Conviene a tutti.',
          side: 'bottom',
        },
      },
    ],
  },
  // Tour inline (non-checklist): id libero, lanciato da `launchInlineTour` alla
  // prima apertura del drawer condiviso Nota/Attivita' ridisegnato. Vedi
  // MD/features/2026-08-10-drawer-note-ui-ux-redesign-proposta.md, sezione
  // "Tour di novita' driver.js" — testi verbatim.
  'drawer-redesign': {
    taskId: 'drawer-redesign',
    completeOnFinish: false,
    steps: [
      {
        element: '[data-tour="drawer-tabs"]',
        popover: {
          title: 'Nota e Attività, un unico posto',
          description: 'Nota e Attività in un unico posto: cambia tab qui.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="drawer-status-toggle"]',
        popover: {
          title: 'Stato richiudibile',
          description:
            "Lo stato del lead o dell'offerta ora è richiudibile: aprilo solo quando vuoi cambiarlo, la nota resta protagonista.",
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="drawer-props"]',
        popover: {
          title: 'Dettagli sempre a portata',
          description: 'I dettagli sono righe di proprietà: clicca un valore per modificarlo.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="drawer-calendar"]',
        popover: {
          title: 'Calendario e invitati',
          description: 'Da qui gestisci evento, invitati e link riunione (Google Meet o Teams).',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="drawer-save"]',
        popover: {
          title: 'Bottone contestuale',
          description: 'Il bottone ti dice sempre cosa sta per fare: Salva nota, Salva attività o Aggiorna.',
          side: 'top',
          align: 'end',
        },
      },
    ],
  },
  // Secondo tour inline: si aggancia alla cronologia (lead/offerta) ridisegnata,
  // lanciato al mount della sezione quando ci sono gia' item in timeline. Vedi
  // MD/features/2026-08-10-drawer-note-ui-ux-redesign-proposta.md, sezione
  // "Tour di novita' driver.js" — testi verbatim.
  'cronologia-redesign': {
    taskId: 'cronologia-redesign',
    completeOnFinish: false,
    steps: [
      {
        // PRIMO step: il composer è la prima cosa che l'utente vede in alto nella cronologia.
        element: '[data-tour="timeline-composer"]',
        popover: {
          title: 'Scrivi al volo',
          description:
            "Scrivi qui una nota o crea un'attività al volo: per calendario e invitati usa \"Vedi tutte le opzioni\".",
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="timeline-days"]',
        popover: {
          title: 'Raggruppate per giorno',
          description: 'Le attività ora sono raggruppate per giorno.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="timeline-card"]',
        popover: {
          title: 'Azioni al volo',
          description: 'Passa il mouse su una card per completare, modificare o eliminare.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="timeline-add"]',
        popover: {
          title: 'Crea dalla cronologia',
          description: 'Da qui crei note, attività e registrazioni.',
          side: 'bottom',
          align: 'end',
        },
      },
    ],
  },
};

export function getTourDefinition(taskId: TaskKey | string): TourDefinition | null {
  return (TOUR_DEFINITIONS as Record<string, TourDefinition | undefined>)[taskId] ?? null;
}
